import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Loader2,
  AlertCircle,
  LogOut,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { proposalService, type OverviewLayoutType } from '@/services/ProposalService';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { PlacesService } from '@/services/PlacesService';
import { VenuePhotoService } from '@/services/VenuePhotoService';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { formatEventDates, formatDateRange } from '@/lib/dateFormatters';
import { formatCurrency } from '@/lib/currencyFormat';
import { 
  getStoredSession, 
  clearSession,
  type StoredSession 
} from '@/services/PublicSessionManager';
import { CoverPage, Overview, ProposalLayout, Destination, Comparison, Agenda, SignOff } from './proposal';
import { calculateVenuePerPerson } from './proposal/budgetCalculation';
import { useProposalTracking } from './proposal/useProposalTracking';
import './proposal/proposal.css';

// ============================================
// Theme Context
// ============================================
type ThemeMode = 'light' | 'dark';

const ThemeContext = createContext<{
  theme: ThemeMode;
  toggleTheme: () => void;
}>({
  theme: 'dark',
  toggleTheme: () => {},
});

function useTheme() {
  return useContext(ThemeContext);
}

// ============================================
// Storage & Session
// ============================================
const THEME_STORAGE_KEY = 'share_theme_preference';

function getStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {}
  return 'dark';
}

function setStoredTheme(theme: ThemeMode) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {}
}

// ============================================
// Theme Toggle Component
// ============================================
function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  
  return (
    <button
      onClick={toggleTheme}
      className={cn(
        "relative w-16 h-8 rounded-full transition-all duration-300 flex items-center",
        isDark 
          ? "bg-gray-700 border border-gray-600" 
          : "bg-gray-200 border border-gray-300"
      )}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <span
        className={cn(
          "absolute w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm",
          isDark 
            ? "left-1 bg-gray-900 text-secondary-400" 
            : "left-[34px] bg-white text-secondary-600"
        )}
      >
        {isDark ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
      </span>
    </button>
  );
}

// ============================================
// Main Proposal View Component
// ============================================
export function ProposalView() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  // Theme state
  const [theme, setTheme] = useState<ThemeMode>(getStoredTheme);
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    setStoredTheme(newTheme);
  };
  const isDark = theme === 'dark';
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkInfo, setLinkInfo] = useState<{ event_name: string; client_name: string; event_id?: string; overview_layout?: OverviewLayoutType | null; organization_id?: string; is_public?: boolean } | null>(null);
  const [overviewLayout, setOverviewLayout] = useState<OverviewLayoutType | null>(null);
  const [session, setSession] = useState<StoredSession | null>(null);
  const [proposalData, setProposalData] = useState<any>(null);
  // Team switch in Budget Manager; absent on older API responses means shown.
  const showBudget = proposalData?.show_budget !== false;
  const [calendlyLink, setCalendlyLink] = useState<string | null>(null);
  const [eventData, setEventData] = useState<EventDTO | null>(null);
  const [destinations, setDestinations] = useState<Array<{ id: string; city: string; country: string; content_city?: string; content_country?: string; heroImage?: string; tagline?: string }>>([]);
  const [approvedVenuesByDestination, setApprovedVenuesByDestination] = useState<Record<string, Array<{
    id: string;
    name: string;
    stars: number;
    image: string;
    images?: string[];
    description?: string;
    roomCount?: number;
    meetingSpaces?: string[];
    priceRange?: string;
    highlights?: string[];
    recommended?: boolean;
    buyout?: boolean;
    allInclusive?: boolean;
    city?: string;
    availableDates?: { start: string; end: string };
    clientNote?: string;
  }>>>({});
  const [venueDataByDestination, setVenueDataByDestination] = useState<Record<string, Array<{
    venue: any; // VenueDetails
    shortlistItem: any; // VenueShortlistItem
  }>>>({});
  const [destinationContentByDestination, setDestinationContentByDestination] = useState<Record<string, any>>({});
  const [activitiesByDestination, setActivitiesByDestination] = useState<Record<string, Array<{
    id: string;
    title: string;
    category: string;
    image: string;
    description?: string;
    duration?: string;
    intensity?: 'Active' | 'Moderate' | 'Relaxed';
  }>>>({});
  const [eventsByDestination, setEventsByDestination] = useState<Record<string, Array<{
    id: string;
    title: string;
    tag: string;
    image: string;
    description?: string;
    participants?: string;
    pricePerPerson?: number;
  }>>>({});
  // Initialize from URL hash — if present, skip cover and go to that section
  const initialHash = window.location.hash.slice(1); // remove '#'
  const hasInitialHash = !!initialHash && initialHash !== 'intro';
  const [showCover, setShowCover] = useState(!hasInitialHash);
  const [activeId, setActiveId] = useState<string>(hasInitialHash ? initialHash : 'intro');
  const [clientShortlist, setClientShortlist] = useState<Array<{
    destinationId: string;
    hotelId: string;
    destinationCity: string;
    destinationCountry: string;
    hotelName: string;
    hotelImage: string;
    estimatedPerPerson?: number;
  }>>([]);

  // Client-engagement tracking (opens, dwell, sections, hotel details).
  // Strictly additive: the proposal loads fine even if tracking is blocked.
  const { trackSection, trackHotelView } = useProposalTracking(token, !!proposalData && !error);

  // Get current user from auth store (for platform user detection)
  const platformUser = useAuthStore((state) => state.user);
  
  // Helper function to extract event data from proposalData
  function extractEventDataFromProposal(proposalDataLocal: any) {
    if (!proposalDataLocal?.event) return;
    
    const event: EventDTO = {
      id: proposalDataLocal.event.id || '',
      client_id: proposalDataLocal.event.client_id || '',
      name: proposalDataLocal.event.name || '',
      slug: proposalDataLocal.event.slug || '',
      start_date: proposalDataLocal.event.start_date || proposalDataLocal.start_date,
      end_date: proposalDataLocal.event.end_date || proposalDataLocal.end_date,
      expected_attendees: proposalDataLocal.event.expected_attendees || proposalDataLocal.event.group_size || proposalDataLocal.expected_attendees,
      budget_min: proposalDataLocal.event.budget_min || proposalDataLocal.budget_min,
      budget_max: proposalDataLocal.event.budget_max || proposalDataLocal.budget_max,
      objective: proposalDataLocal.event.objective || proposalDataLocal.objective,
      temperature_unit: proposalDataLocal.event.temperature_unit || 'celsius',
      distance_unit: proposalDataLocal.event.distance_unit || 'kilometers',
      date_type: proposalDataLocal.event.date_type,
      event_month: proposalDataLocal.event.event_month,
      date_display_override: proposalDataLocal.event.date_display_override,
    };
    setEventData(event);
  }
  
  // Load Calendly link from organization settings
  async function loadCalendlyLink(organizationId: string) {
    try {
      const response = await fetch(`/api/public/organization/${organizationId}/proposal-settings`, {
        credentials: 'include',
      });
      if (response.ok) {
        const result = await response.json();
        setCalendlyLink(result.data?.proposal_review_call_link || null);
      }
    } catch (err) {
      console.error('[ProposalView] Failed to load Calendly link:', err);
      // Use default fallback
      setCalendlyLink('https://calendly.com/moniker-partners/review-call');
    }
  }

  // Load client shortlist
  async function loadClientShortlist(
    shareLinkToken: string, 
    sessionId?: string,
    venuesMap?: Record<string, Array<{
      id: string;
      name: string;
      stars: number;
      image: string;
      images?: string[];
      description?: string;
      roomCount?: number;
      meetingSpaces?: string[];
      priceRange?: string;
      highlights?: string[];
      recommended?: boolean;
      availableDates?: { start: string; end: string };
    }>>,
    venueDataMap?: Record<string, Array<{
      venue: any;
      shortlistItem: any;
    }>>
  ) {
    try {
      const shortlist = await proposalService.getClientShortlist(shareLinkToken, sessionId);
      // Use provided venues map or fall back to state
      const venuesToUse = venuesMap || approvedVenuesByDestination;
      const venueDataToUse = venueDataMap || venueDataByDestination;
      
      // Enrich with images from venues if available
      const enrichedShortlist = await Promise.all(shortlist.map(async (item) => {
        // Try to find the hotel in venues to get the actual image
        // This ensures we use the exact same image as displayed on the destination page
        const venues = venuesToUse[item.destination_id] || [];
        let hotelImage = item.hotel_image;
        
        // First try to get from venues (same source as destination page)
        const venueData = venues.find(v => v.id === item.hotel_id);
        if (venueData) {
          // Use the same image property that's used on the destination page
          // Priority: image property (first image) > images[0] > fallback
          hotelImage = venueData.image || venueData.images?.[0] || hotelImage;
        } else {
          // If not found in venues, try to get from venueDataMap and fetch photo if needed
          const venueInfo = venueDataToUse[item.destination_id]?.find(
            v => v.venue.id === item.hotel_id
          );
          if (venueInfo?.venue?.id) {
            try {
              const venuePhotoSvc = new VenuePhotoService();
              const data = await venuePhotoSvc.getResolvedPhotos(
                venueInfo.venue.id,
                800,
                shareLinkToken,
                sessionId
              );
              if (data.photos && data.photos.length > 0) {
                hotelImage = data.photos[0].url;
              }
            } catch (err) {
              console.error('[ProposalView] Failed to fetch photo for shortlist item:', err);
            }
          }
        }
        
        return {
          destinationId: item.destination_id,
          hotelId: item.hotel_id,
          destinationCity: item.destination_city,
          destinationCountry: item.destination_country,
          hotelName: item.hotel_name,
          hotelImage: hotelImage || '', // No fallback - will show empty/placeholder if no image available
          estimatedPerPerson: item.estimated_per_person,
        };
      }));
      setClientShortlist(enrichedShortlist);
    } catch (err) {
      console.error('[ProposalView] Failed to load client shortlist:', err);
      // Set empty array on error
      setClientShortlist([]);
    }
  }

  // Handle adding to client shortlist
  async function handleAddToClientShortlist(hotelId: string, destinationId: string) {
    if (!token || !session?.sessionId) {
      console.error('Cannot add to shortlist: missing token or session ID');
      return;
    }
    try {
      await proposalService.addToClientShortlist(token, hotelId, destinationId, session.sessionId);
      // Reload shortlist with current venue data
      await loadClientShortlist(token, session.sessionId, approvedVenuesByDestination, venueDataByDestination);
    } catch (err) {
      console.error('[ProposalView] Failed to add to shortlist:', err);
    }
  }

  // Helper function to load event data and destinations
  async function loadEventData(eventId: string, shareLinkToken?: string, sessionId?: string, proposalDataLocal?: any) {
    try {
      const eventsSvc = new EventsService();
      
      // Try to fetch event data - if it fails (auth required), extract from proposalData
      let event: EventDTO | null = null;
      try {
        event = await eventsSvc.get(eventId);
        setEventData(event);
      } catch (err: any) {
        // If direct fetch fails, try to extract from proposalData
        if (proposalDataLocal?.event) {
          extractEventDataFromProposal(proposalDataLocal);
        } else if (proposalData?.event) {
          extractEventDataFromProposal(proposalData);
        }
      }
      
      // Always render the client payload, including for signed-in planners.
      // Auth-dependent destination trees used to leak old child/sibling lists.
      const dataSource = proposalDataLocal || proposalData;
      const destList = (dataSource?.grouped_by_city || []).map((group: any) => {
        const childIds = (group.destinations || []).map((d: any) => d.destination.id);
        return {
          id: group.content_destination_id || childIds[0] || `group-${group.city}`,
          activity_source_id: group.content_destination_id as string | null,
          weather_destination_id: childIds[0],
          city: group.display_name || group.city,
          country: group.country,
          content_city: group.destinations?.[0]?.destination.city || group.city,
          content_country: group.country,
          heroImage: undefined as string | undefined,
          tagline: undefined as string | undefined,
          _childDestIds: childIds as string[],
        };
      });
      setDestinations(destList);
      
      // ── Fetch content+weather, activities, events for ALL destinations in parallel ──

      // Helper: fetch content + weather for a single destination
      const fetchContentAndWeather = async (dest: typeof destList[number]) => {
        const displayCity = dest.city;
        const originalCity = (dest as any).content_city || dest.city;
        const originalCountry = (dest as any).content_country || dest.country;
        try {
          let content = shareLinkToken
            ? await proposalService.getDestinationContentForProposal(shareLinkToken, displayCity, dest.country).catch(() => null)
            : await proposalService.getDestinationContent(displayCity, dest.country).catch(() => null);
          if (!content && originalCity !== displayCity) {
            content = shareLinkToken
              ? await proposalService.getDestinationContentForProposal(shareLinkToken, originalCity, originalCountry).catch(() => null)
              : await proposalService.getDestinationContent(originalCity, originalCountry).catch(() => null);
          }
          let mergedContent: Record<string, unknown> = content ? { ...content } : { city: dest.city, country: dest.country };
          try {
            const weather = shareLinkToken
              ? await proposalService.getResearchWeatherForProposal(shareLinkToken, dest.weather_destination_id)
              : await proposalService.getResearchWeather(dest.weather_destination_id);
            if (weather) {
              mergedContent = {
                ...mergedContent,
                day_avg_temperature: weather.day_avg_temperature,
                night_avg_temperature: weather.night_avg_temperature,
                rainfall: weather.rainfall,
                rainy_days: weather.rainy_days,
                humidity: weather.humidity,
                weather_description: weather.weather_description,
                travel_tip_weather: weather.travel_tip_weather,
                travel_tip_packing: weather.travel_tip_packing,
                travel_tip_local_events: weather.travel_tip_local_events,
                annual_climate_data: weather.annual_climate_data,
                travel_month: weather.travel_month,
              };
            }
          } catch (weatherErr) {
            console.debug(`[ProposalView] No weather for ${dest.city}:`, (weatherErr as Error).message);
          }
          return { id: dest.id, content: mergedContent };
        } catch (err) {
          console.error(`[ProposalView] Failed to fetch content for ${displayCity}, ${dest.country}:`, err);
          return { id: dest.id, content: null };
        }
      };

      // Helper: map raw activities to display format
      const categoryMap: Record<string, string> = {
        'culture': 'Culture', 'adventure': 'Adventure', 'food_drink': 'Food & Drink',
        'wellness': 'Wellness', 'entertainment': 'Entertainment', 'nature': 'Nature',
        'team_building': 'Team Building', 'sightseeing': 'Sightseeing',
        'water_sports': 'Water Sports', 'other': 'Other',
      };
      const intensityMap: Record<string, 'Active' | 'Moderate' | 'Relaxed'> = {
        'active': 'Active', 'moderate': 'Moderate', 'relaxed': 'Relaxed',
      };

      // Helper: fetch activities for a single destination
      const fetchActivities = async (dest: typeof destList[number]) => {
        try {
          if (!dest.activity_source_id) return {id:dest.id,activities:[]};
          let activities = shareLinkToken
            ? await proposalService.listResearchActivitiesForProposal(shareLinkToken, dest.id).catch(() => [])
            : await proposalService.listResearchActivities(dest.id, false).catch(() => []).then(items =>
                items.map(ra => ({ ...ra, is_active: (ra as any).is_included, name: ra.name } as any))
              );
          const mapped = activities
            .filter(a => a.is_active)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map(activity => ({
              id: activity.id,
              title: activity.name,
              category: activity.category ? categoryMap[activity.category] || activity.category : 'Other',
              image: activity.images?.length > 0
                ? activity.images[0].url
                : 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80',
              description: activity.description || undefined,
              duration: activity.duration_hours
                ? `${activity.duration_hours}${activity.duration_hours === 1 ? ' hour' : ' hours'}`
                : undefined,
              intensity: activity.intensity ? intensityMap[activity.intensity] : undefined,
            }));
          return { id: dest.id, activities: mapped };
        } catch (err) {
          console.error(`[ProposalView] Failed to fetch activities for ${dest.city}:`, err);
          return { id: dest.id, activities: [] };
        }
      };

      // Helper: fetch events for a single destination
      // Match the actual Edit Destination event modal: exact display city,
      // never an original-city fallback after the user clears the list.
      const fetchEvents = async (dest: typeof destList[number]) => {
        try {
          const tagMap: Record<string, string> = {
            'off_venue': 'Offsite Venue', 'welcome_reception': 'Welcome Reception',
            'final_dinner': 'Final Dinner', 'nightlife': 'Nightlife',
          };
          const displayCity = dest.city;
          let events = shareLinkToken
            ? await proposalService.listEventsForProposal(shareLinkToken, displayCity, dest.country).catch(() => [])
            : await proposalService.listEvents(displayCity, dest.country, false).catch(() => []);
          const mapped = events
            .filter(e => e.is_active)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map(event => ({
              id: event.id,
              title: event.name,
              tag: event.tag ? tagMap[event.tag] || event.tag : 'Event',
              image: event.images?.length > 0
                ? event.images[0].url
                : 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80',
              description: event.description || undefined,
              participants: event.participants || undefined,
              pricePerPerson: event.estimated_price_per_person || undefined,
            }));
          return { id: dest.id, events: mapped };
        } catch (err) {
          console.error(`[ProposalView] Failed to fetch events for ${dest.city}:`, err);
          return { id: dest.id, events: [] };
        }
      };

      // Fire all destination data fetches in parallel
      const [contentResults, activityResults, eventResults] = await Promise.all([
        Promise.all(destList.map(fetchContentAndWeather)),
        Promise.all(destList.map(fetchActivities)),
        Promise.all(destList.map(fetchEvents)),
      ]);

      // Build maps from results
      const contentMap: Record<string, any> = {};
      for (const r of contentResults) if (r.content) contentMap[r.id] = r.content;
      setDestinationContentByDestination(contentMap);

      const activitiesMap: Record<string, any[]> = {};
      for (const r of activityResults) activitiesMap[r.id] = r.activities;
      setActivitiesByDestination(activitiesMap);

      const eventsMap: Record<string, any[]> = {};
      for (const r of eventResults) eventsMap[r.id] = r.events;
      setEventsByDestination(eventsMap);
      
      // Fetch approved venues for each destination
      // Use proposalDataLocal fallback when auth-required API fails (e.g. public link viewers)
      try {
        let approvedProposals: {
          grouped_by_city?: Array<{ city: string; country: string; destinations: Array<{ destination: { id: string; city: string; country: string }; shortlist: Array<{ venue: any; approval_status?: string | null; override_lowest_room_rate?: number; cvent_lowest_room_rate?: number; cvent_currency_code?: string; is_recommended?: boolean; is_buyout?: boolean; is_all_inclusive?: boolean; comparison_budget_note?: string | null }> }> }>;
          destinations?: Array<{ destination: { id: string; city: string; country: string }; shortlist: Array<{ venue: any; approval_status?: string | null; override_lowest_room_rate?: number; cvent_lowest_room_rate?: number; cvent_currency_code?: string; is_recommended?: boolean; is_buyout?: boolean; is_all_inclusive?: boolean; comparison_budget_note?: string | null }> }>;
        };
        approvedProposals = {
          grouped_by_city: dataSource?.grouped_by_city || [],
          destinations: dataSource?.destinations || [],
        };

        // Exclude venues the team has hidden from the client proposal.
        // The admin `getSubmittedProposals` endpoint returns hidden venues
        // (so the admin tool can still display them with a "Hidden" badge);
        // the public `/api/public/proposal/:token/data` endpoint already
        // filters them out. We filter here so the client-facing ProposalView
        // is consistent regardless of which endpoint served the data.
        const notHidden = (item: any) => !item?.is_hidden;
        if (approvedProposals.grouped_by_city) {
          approvedProposals.grouped_by_city = approvedProposals.grouped_by_city
            .map(g => ({
              ...g,
              destinations: (g.destinations || [])
                .map(d => ({ ...d, shortlist: (d.shortlist || []).filter(notHidden) }))
                .filter(d => d.shortlist.length > 0),
            }))
            .filter(g => g.destinations.length > 0);
        }
        if (approvedProposals.destinations) {
          approvedProposals.destinations = approvedProposals.destinations
            .map(d => ({ ...d, shortlist: (d.shortlist || []).filter(notHidden) }))
            .filter(d => d.shortlist.length > 0);
        }
        const venuePhotoService = new VenuePhotoService();

        // Map approved venues by destination ID
        const venuesMap: Record<string, Array<{
          id: string;
          name: string;
          stars: number;
          image: string;
          images?: string[];
          description?: string;
          roomCount?: number;
          meetingSpaces?: string[];
          priceRange?: string;
          highlights?: string[];
          recommended?: boolean;
          buyout?: boolean;
          allInclusive?: boolean;
          availableDates?: { start: string; end: string };
          clientNote?: string;
        }>> = {};

        // Store full venue data for modal
        const venueDataMap: Record<string, Array<{
          venue: any;
          shortlistItem: any;
        }>> = {};

        // Helper function to fetch photo for a venue (uses resolved-photos: custom if exist, else Google)
        const fetchVenuePhoto = async (venue: any): Promise<string> => {
          if (!venue.id) {
            return '';
          }

          try {
            const data = await venuePhotoService.getResolvedPhotos(
              venue.id,
              800,
              shareLinkToken,
              sessionId
            );
            if (data.photos && data.photos.length > 0) {
              return data.photos[0].url;
            }
          } catch (err) {
            console.error('[ProposalView] Failed to fetch photo for venue:', venue.id, err);
          }

          return '';
        };
        
        // Helper: build venue display object from shortlist item + photo URL
        const buildVenueDisplay = (item: any, imageUrl: string) => {
          const venue = item.venue;
          let priceRange: string | undefined;
          const displayLowestRate = item.override_lowest_room_rate ?? item.cvent_lowest_room_rate;
          if (displayLowestRate) {
            const currency = item.cvent_currency_code || 'USD';
            priceRange = `${formatCurrency(Math.round(displayLowestRate), currency)}/night`;
          }
          const meetingSpaces: string[] | undefined = venue.meeting_spaces
            ? Array(venue.meeting_spaces).fill(0).map((_, i) => `Meeting Space ${i + 1}`)
            : undefined;

          // Extract availability dates — CVENT first, then manual proposal fallback
          let availableDates: { start: string; end: string } | undefined;
          const cventData = item.cvent_proposal_data_jsonb;
          const cventProposal = cventData?.proposal || cventData || {};
          const eventAvail = cventProposal.eventAvailability || {};
          const startDate = eventAvail.startDate || item.proposal_check_in_date;
          const endDate = eventAvail.endDate || item.proposal_check_out_date;
          if (startDate && endDate) {
            availableDates = { start: startDate, end: endDate };
          }

          return {
            id: venue.id,
            name: venue.property_name,
            stars: venue.star_rating || 4,
            image: imageUrl,
            images: [imageUrl],
            description: venue.tripadvisor_reviews_json?.[0]?.text?.substring(0, 200) || undefined,
            roomCount: venue.number_of_rooms || undefined,
            meetingSpaces,
            priceRange,
            availableDates,
            highlights: [
              venue.spa_facilities ? 'Spa' : null,
              venue.beach_access === 'beachfront' ? 'Beachfront' : venue.beach_access === 'beach_access' ? 'Beach Access' : null,
              venue.all_inclusive_available ? 'All-Inclusive Available' : null,
              venue.golf_course === 'onsite' ? 'Onsite Golf' : venue.golf_course === 'nearby' ? 'Nearby Golf' : null,
            ].filter(Boolean) as string[],
            recommended: item.is_recommended || false,
            buyout: item.is_buyout || false,
            allInclusive: item.is_all_inclusive || false,
            city: venue.city || undefined,
            // Already filtered server-side: present only when marked client-facing.
            clientNote: item.notes?.trim() || undefined,
          };
        };

        // Process grouped_by_city structure — collect all venue photo fetches across ALL groups
        // then fire them in one big Promise.all
        if (approvedProposals.grouped_by_city) {
          // First pass: collect shortlists and store venue data (sync)
          const groupEntries: Array<{ groupKey: string; shortlist: any[] }> = [];
          for (const cityGroup of approvedProposals.grouped_by_city) {
            const groupKey = (cityGroup as any).content_destination_id || cityGroup.destinations?.[0]?.destination?.id || `group-${cityGroup.city}`;
            const allApprovedShortlist: any[] = [];
            for (const destGroup of cityGroup.destinations) {
              const approved = destGroup.shortlist.filter(
                (item: any) => item.approval_status === 'approved' || item.venue
              );
              allApprovedShortlist.push(...approved);
              const destId = destGroup.destination.id;
              venueDataMap[destId] = approved.map((item: any) => ({
                venue: item.venue, shortlistItem: item,
              }));
            }
            venueDataMap[groupKey] = allApprovedShortlist.map(item => ({
              venue: item.venue, shortlistItem: item,
            }));
            groupEntries.push({ groupKey, shortlist: allApprovedShortlist });
          }

          // Second pass: fetch ALL venue photos across all groups in parallel
          const allVenueItems = groupEntries.flatMap(g => g.shortlist);
          const allPhotos = await Promise.all(
            allVenueItems.map(item => fetchVenuePhoto(item.venue))
          );

          // Third pass: slice photos back into groups and build display objects
          let photoIdx = 0;
          for (const { groupKey, shortlist } of groupEntries) {
            const groupPhotos = allPhotos.slice(photoIdx, photoIdx + shortlist.length);
            photoIdx += shortlist.length;
            venuesMap[groupKey] = shortlist.map((item, i) => buildVenueDisplay(item, groupPhotos[i]));
          }
        }

        // Also process flat destinations structure as fallback
        if (approvedProposals.destinations && Object.keys(venuesMap).length === 0) {
          // Collect all items, fetch all photos in one batch
          const flatEntries: Array<{ destId: string; shortlist: any[] }> = [];
          for (const destGroup of approvedProposals.destinations) {
            const destId = destGroup.destination.id;
            const approvedShortlist = destGroup.shortlist.filter(
              item => item.approval_status === 'approved' || item.venue
            );
            venueDataMap[destId] = approvedShortlist.map(item => ({
              venue: item.venue, shortlistItem: item,
            }));
            flatEntries.push({ destId, shortlist: approvedShortlist });
          }

          const allFlatItems = flatEntries.flatMap(e => e.shortlist);
          const allFlatPhotos = await Promise.all(
            allFlatItems.map(item => fetchVenuePhoto(item.venue))
          );

          let flatIdx = 0;
          for (const { destId, shortlist } of flatEntries) {
            const photos = allFlatPhotos.slice(flatIdx, flatIdx + shortlist.length);
            flatIdx += shortlist.length;
            venuesMap[destId] = shortlist.map((item, i) => buildVenueDisplay(item, photos[i]));
          }
        }
        
        // Map region IDs to merged child venues.
        // destList may have entries with _childDestIds (region entries) whose id is the
        // region ID, while venuesMap is keyed by child destination IDs. Bridge the gap.
        for (const dest of destList) {
          if ((dest as any)._childDestIds && !venuesMap[dest.id]) {
            const merged: any[] = [];
            const mergedData: any[] = [];
            for (const childId of (dest as any)._childDestIds) {
              if (venuesMap[childId]) merged.push(...venuesMap[childId]);
              if (venueDataMap[childId]) mergedData.push(...venueDataMap[childId]);
            }
            if (merged.length > 0) venuesMap[dest.id] = merged;
            if (mergedData.length > 0) venueDataMap[dest.id] = mergedData;
          }
        }

        // Drop destinations that have no visible approved venues left.
        // This covers (a) destinations where every approved venue is hidden,
        // and (b) pre-existing destinations that never had any approved
        // venues — in both cases there's nothing useful to show on the
        // client proposal, so hide the tab entirely.
        const destListWithVenues = destList.filter((dest: any) => {
          if (venuesMap[dest.id] && venuesMap[dest.id].length > 0) return true;
          const childIds = (dest as any)._childDestIds as string[] | undefined;
          if (childIds && childIds.some(cid => venuesMap[cid]?.length > 0)) return true;
          return false;
        });
        if (destListWithVenues.length !== destList.length) {
          setDestinations(destListWithVenues);
        }

        setApprovedVenuesByDestination(venuesMap);
        setVenueDataByDestination(venueDataMap);
        
        // Return venues data so we can use it immediately for shortlist
        return { venuesMap, venueDataMap };
      } catch (err: any) {
        console.error('[ProposalView] Failed to load approved venues:', err);
        // Don't set error - venues are optional
        return { venuesMap: {}, venueDataMap: {} };
      }
    } catch (err: any) {
      console.error('[ProposalView] Failed to load event data:', err);
      // Don't set error - this is optional data
      return { venuesMap: {}, venueDataMap: {} };
    }
  }
  
  // Load data
  useEffect(() => {
    async function init() {
      if (!token) {
        setError('Invalid link');
        setLoading(false);
        return;
      }
      
      // Check if link is public first (treat null/undefined as public)
      try {
        const linkInfo = await proposalService.getPublicLinkInfo(token);
        if (linkInfo.is_public !== false) {
          // Public link - no authentication required, load data directly
          try {
            const data = await proposalService.getProposalData(token);
            setProposalData(data);
            setSession({ 
              token, 
              sessionId: 'public-link', 
              email: 'Public Access',
              expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            });
            
            setLinkInfo(linkInfo);
            setOverviewLayout(linkInfo.overview_layout || null);
            
            // Extract event data from proposalData if available
            if (data?.event) {
              extractEventDataFromProposal(data);
            }
            
            // Load Calendly link and event data in parallel
            const calendlyPromise = linkInfo.organization_id
              ? loadCalendlyLink(linkInfo.organization_id)
              : Promise.resolve(setCalendlyLink('https://calendly.com/moniker-partners/review-call'));

            let venuesData: { venuesMap: any; venueDataMap: any } | null = null;
            const eventDataPromise = linkInfo.event_id
              ? loadEventData(linkInfo.event_id, token, 'public-link', data)
              : Promise.resolve((() => { if (data) extractEventDataFromProposal(data); return null; })());

            const [, eventResult] = await Promise.all([calendlyPromise, eventDataPromise]);
            venuesData = eventResult;
            
            // Load client shortlist AFTER venues are loaded
            try {
              await loadClientShortlist(
                token, 
                'public-link',
                venuesData?.venuesMap,
                venuesData?.venueDataMap
              );
            } catch (err) {
              console.error('[ProposalView] Failed to load client shortlist:', err);
            }
            
            setLoading(false);
            return;
          } catch (err: any) {
            console.error('Failed to load proposal data for public link:', err);
            setError('Failed to load proposal data');
            setLoading(false);
            return;
          }
        }
      } catch (err: any) {
        // If we can't get link info, fall through to normal auth flow
        console.log('[ProposalView] Could not check link info, falling back to auth:', err.message);
      }
      
      // First, if user is logged in on the platform, try to load data directly
      if (platformUser) {
        try {
          // Try to load data - will succeed if user has org access
          const data = await proposalService.getProposalData(token);
          setProposalData(data);
          setSession({ 
            token, 
            sessionId: 'platform-user', 
            email: platformUser.email || 'Platform User',
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          });
          
          // Extract event data from proposalData if available
          if (data?.event) {
            extractEventDataFromProposal(data);
          }
          
          // Fetch link info for display
          try {
            const info = await proposalService.getPublicLinkInfo(token);
            setLinkInfo(info);
            setOverviewLayout(info.overview_layout || null);
            
            // Load Calendly link and event data in parallel
            const calendlyPromisePU = info.organization_id
              ? loadCalendlyLink(info.organization_id)
              : Promise.resolve(setCalendlyLink('https://calendly.com/moniker-partners/review-call'));

            let venuesData: { venuesMap: any; venueDataMap: any } | null = null;
            const eventDataPromisePU = info.event_id
              ? loadEventData(info.event_id, token, 'platform-user', data)
              : Promise.resolve((() => { if (data) extractEventDataFromProposal(data); return null; })());

            const [, eventResultPU] = await Promise.all([calendlyPromisePU, eventDataPromisePU]);
            venuesData = eventResultPU;
            
            // Load client shortlist AFTER venues are loaded so we can use their images
            // Pass venues data directly to avoid state timing issues
            try {
              await loadClientShortlist(
                token, 
                'platform-user',
                venuesData?.venuesMap,
                venuesData?.venueDataMap
              );
            } catch (err) {
              console.error('[ProposalView] Failed to load client shortlist:', err);
            }
          } catch {}
          
          setLoading(false);
          return;
        } catch (err: any) {
          // Platform user doesn't have org access, fall through to OTP session
        }
      }
      
      // Fall back to OTP session-based access
      const storedSession = getStoredSession(token);
      if (!storedSession) {
        navigate(`/p/${token}`, { replace: true });
        return;
      }
      
      setSession(storedSession);
      
      try {
        // Verify session is still valid
        const check = await proposalService.checkSession(token, storedSession.sessionId);
        if (!check.is_valid) {
          if (token) clearSession(token);
          navigate(`/p/${token}`, { replace: true });
          return;
        }
        
        // Try to load proposal data
        let proposalDataLoaded: any = null;
        try {
          proposalDataLoaded = await proposalService.getProposalData(token, storedSession.sessionId);
          setProposalData(proposalDataLoaded);
        } catch (err: any) {
          // If data endpoint doesn't exist yet, that's okay - we'll use linkInfo
        }
        
        // Fetch link info for display
        try {
          const info = await proposalService.getPublicLinkInfo(token);
          setLinkInfo(info);
          setOverviewLayout(info.overview_layout || null);
          
          // Load Calendly link and event data in parallel
          const calendlyPromiseOTP = info.organization_id
            ? loadCalendlyLink(info.organization_id)
            : Promise.resolve(setCalendlyLink('https://calendly.com/moniker-partners/review-call'));

          let venuesData: { venuesMap: any; venueDataMap: any } | null = null;
          const eventDataPromiseOTP = info.event_id
            ? loadEventData(info.event_id, token, storedSession.sessionId, proposalDataLoaded)
            : Promise.resolve((() => { if (proposalDataLoaded) extractEventDataFromProposal(proposalDataLoaded); return null; })());

          const [, eventResultOTP] = await Promise.all([calendlyPromiseOTP, eventDataPromiseOTP]);
          venuesData = eventResultOTP;
          
          // Load client shortlist AFTER venues are loaded so we can use their images
          // Pass venues data directly to avoid state timing issues
          try {
            await loadClientShortlist(
              token, 
              storedSession.sessionId,
              venuesData?.venuesMap,
              venuesData?.venueDataMap
            );
          } catch (err) {
            console.error('[ProposalView] Failed to load client shortlist:', err);
            // Don't fail the whole page if shortlist fails
          }
        } catch {}
        
        setLoading(false);
      } catch (err: any) {
        console.error('Failed to load proposal data:', err);
        if (err.message?.includes('session') || err.message?.includes('401')) {
          if (token) clearSession(token);
          navigate(`/p/${token}`, { replace: true });
        } else {
          setError('Failed to load proposal data');
        }
        setLoading(false);
      }
    }
    
    init();
  }, [token, navigate, platformUser]);

  // Enrich client shortlist with per-person from same calculation as Comparison tab
  useEffect(() => {
    if (
      !showBudget ||
      !clientShortlist.length ||
      !linkInfo?.event_id ||
      !token ||
      !venueDataByDestination ||
      Object.keys(venueDataByDestination).length === 0
    ) {
      return;
    }

    const tokenVal = token;
    const eventIdVal = linkInfo.event_id;
    let cancelled = false;
    const run = async () => {
      try {
        const [fullBudgetRes, exchangeRatesRes] = await Promise.all([
          proposalService.getFullBudget(tokenVal, eventIdVal, session?.sessionId),
          proposalService.getExchangeRates(),
        ]);
        if (cancelled) return;

        const fullBudget = fullBudgetRes?.budget ?? fullBudgetRes;
        const exchangeRates = exchangeRatesRes?.rates ?? exchangeRatesRes;

        const breakdowns: Record<string, any> = {};
        for (const item of clientShortlist) {
          const venueInfo = venueDataByDestination[item.destinationId]?.find(
            (v: { venue: { id: string } }) => v.venue.id === item.hotelId
          );
          if (venueInfo?.shortlistItem?.id) {
            try {
              const data = await proposalService.getVenueBudgetBreakdown(
                tokenVal,
                venueInfo.shortlistItem.id,
                session?.sessionId
              );
              breakdowns[item.hotelId] = data;
            } catch {
              // Skip if fetch fails
            }
          }
        }
        if (cancelled) return;

        const groupSize = eventData?.expected_attendees || 50;
        const enriched = clientShortlist.map((item) => {
          const computed = calculateVenuePerPerson(
            item.hotelId,
            item.destinationId,
            breakdowns[item.hotelId],
            fullBudget,
            exchangeRates,
            groupSize,
            venueDataByDestination,
            destinationContentByDestination
          );
          return {
            ...item,
            estimatedPerPerson: computed ?? item.estimatedPerPerson,
          };
        });

        const hasChange = enriched.some(
          (e, i) => e.estimatedPerPerson !== clientShortlist[i].estimatedPerPerson
        );
        if (hasChange && !cancelled) {
          setClientShortlist(enriched);
        }
      } catch (err) {
        console.error('[ProposalView] Failed to enrich shortlist with per-person:', err);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    showBudget,
    clientShortlist,
    venueDataByDestination,
    destinationContentByDestination,
    linkInfo?.event_id,
    token,
    session?.sessionId,
    eventData?.expected_attendees,
  ]);

  // Handle browser back/forward for hash-based section navigation
  useEffect(() => {
    const onPopState = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        setActiveId(hash);
        setShowCover(false);
      } else {
        setActiveId('intro');
        setShowCover(true);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function handleLogout() {
    if (token) {
      clearSession(token);
    }
    navigate(`/p/${token}`, { replace: true });
  }
  
  // Loading state
  if (loading) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center",
        isDark ? "bg-gray-950" : "bg-gray-50"
      )}>
        <div className="text-center">
          <Loader2 className={cn("w-10 h-10 animate-spin mx-auto mb-4", isDark ? "text-secondary-500" : "text-primary-600")} />
          <p className={isDark ? "text-gray-400" : "text-gray-600"}>Loading proposal...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error && !linkInfo) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center p-4",
        isDark ? "bg-gray-950" : "bg-gray-50"
      )}>
        <div className={cn(
          "max-w-md w-full p-8 text-center rounded-2xl border",
          isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"
        )}>
          <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-error-600" />
          </div>
          <h1 className={cn("text-xl font-semibold mb-2", isDark ? "text-white" : "text-gray-900")}>
            Unable to Load
          </h1>
          <p className={cn("mb-6", isDark ? "text-gray-400" : "text-gray-600")}>
            {error || 'Something went wrong'}
          </p>
          <Button variant="secondary" onClick={() => navigate(`/p/${token}`)}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  // Generate dynamic meta tags
  const eventName = proposalData?.event?.name || linkInfo?.event_name || '';
  const clientName = proposalData?.event?.client_name || linkInfo?.client_name || '';
  const title = `Proposal for ${eventName}${clientName ? ` for ${clientName}` : ''} | Moniker Partners`;
  const description = `Proposal for ${eventName}${clientName ? ` for ${clientName}` : ''} | Moniker Partners`;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  // Helper to get objective label from value
  const getObjectiveLabel = (value: string): string => {
    const objectiveMap: Record<string, string> = {
      'strategic_alignment': 'Strategic Alignment',
      'cross_department_bonding': 'Cross-department Bonding',
      'reward_recognition': 'Reward & Recognition',
      'team_building': 'Team Building',
      'training_development': 'Training & Development',
      'networking': 'Networking',
      'celebration': 'Celebration',
    };
    return objectiveMap[value] || value;
  };
  
  // Format budget range helper
  const formatBudgetRange = (min?: number, max?: number) => {
    if (!min && !max) return undefined;
    if (min && max) {
      return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k`;
    }
    if (min) return `$${(min / 1000).toFixed(0)}k+`;
    if (max) return `Up to $${(max / 1000).toFixed(0)}k`;
    return undefined;
  };
  
  // Extract proposal meta data - prioritize eventData from EventsService
  const proposalMeta = {
    event_name: eventName,
    client_name: clientName,
    dates: (eventData ? formatEventDates(eventData, true) : undefined)
      || proposalData?.event?.dates 
      || (proposalData?.event?.start_date && proposalData?.event?.end_date ? formatDateRange(proposalData.event.start_date, proposalData.event.end_date) : undefined)
      || (proposalData?.start_date && proposalData?.end_date ? formatDateRange(proposalData.start_date, proposalData.end_date) : undefined)
      || undefined,
    budget_range: formatBudgetRange(eventData?.budget_min || undefined, eventData?.budget_max || undefined)
      || proposalData?.event?.budget_range 
      || formatBudgetRange(proposalData?.event?.budget_min, proposalData?.event?.budget_max)
      || formatBudgetRange(proposalData?.budget_min, proposalData?.budget_max)
      || (proposalData?.event?.budget ? `$${(proposalData.event.budget / 1000).toFixed(0)}k` : undefined)
      || undefined,
    group_size: (eventData?.expected_attendees ? `${eventData.expected_attendees} Pax` : undefined)
      || proposalData?.event?.group_size 
      || (proposalData?.event?.expected_attendees ? `${proposalData.event.expected_attendees} Pax` : undefined)
      || (proposalData?.expected_attendees ? `${proposalData.expected_attendees} Pax` : undefined)
      || (proposalData?.event?.attendees ? `${proposalData.event.attendees} Pax` : undefined)
      || undefined,
    objectives: eventData?.objective 
      ? [getObjectiveLabel(eventData.objective)]
      : proposalData?.event?.objectives 
      || proposalData?.event?.goals 
      || proposalData?.objectives
      || proposalData?.goals
      || [],
  };
  
  // Use destinations from state (loaded from ResearchService) or fallback to proposalData
  const displayDestinations = destinations.length > 0 ? destinations : (
    proposalData?.grouped_by_city?.map((g: any, idx: number) => ({
      id: g.destinations?.[0]?.destination?.id || `dest-${idx}`,
      city: g.display_name || g.city || '',
      country: g.country || '',
      heroImage: g.hero_image || g.heroImage,
      tagline: g.tagline || g.description,
    })).filter((d: any) => d.city && d.country)
    || proposalData?.destinations?.map((d: any, idx: number) => ({
      id: d.id || d.destination_id || `dest-${idx}`,
      city: d.destination?.display_city || d.destination?.display_name || d.city || d.name || d.destination?.city || '',
      country: d.destination?.display_country || d.country || d.destination?.country || '',
      heroImage: d.hero_image || d.heroImage || d.images?.[0],
      tagline: d.tagline || d.description,
    })).filter((d: any) => d.city && d.country)
    || proposalData?.approved_destinations?.map((d: any, idx: number) => ({
      id: d.id || `dest-${idx}`,
      city: d.display_name || d.city || d.name || '',
      country: d.country || '',
      heroImage: d.hero_image || d.heroImage,
      tagline: d.tagline || d.description,
    })).filter((d: any) => d.city && d.country)
    || []
  );
  
  // Filter destinations to only show those with approved venues (active destinations)
  const destinationsWithApprovedVenues = displayDestinations.filter((dest: { id: string; city: string; country: string; heroImage?: string; tagline?: string }) => {
    const approvedVenues = approvedVenuesByDestination[dest.id];
    return approvedVenues && approvedVenues.length > 0;
  });

  // Helper function to get first image for a destination
  const getDestinationFirstImage = (destId: string): string | undefined => {
    const content = destinationContentByDestination[destId];
    if (content?.images && content.images.length > 0) {
      const firstImage = content.images[0];
      return typeof firstImage === 'string' ? firstImage : firstImage.url;
    }
    // Fallback to heroImage from destination data
    const dest = displayDestinations.find((d: { id: string; city: string; country: string; heroImage?: string; tagline?: string }) => d.id === destId);
    return dest?.heroImage;
  };

  // Extract hero images from first uploaded image of each destination
  // Only include destinations that have images, skip those without
  const heroImages = displayDestinations
    .map((dest: { id: string; city: string; country: string; heroImage?: string; tagline?: string }) => {
      return getDestinationFirstImage(dest.id);
    })
    .filter((img: string | undefined | null): img is string => img !== undefined && img !== null);

  // Hero images for cover page - only from active destinations
  const heroImagesForCover = destinationsWithApprovedVenues
    .map((dest: { id: string }) => getDestinationFirstImage(dest.id))
    .filter((img: string | undefined | null): img is string => img !== undefined && img !== null);

  // Handle navigation — sync activeId with URL hash
  const handleNavigate = (id: string) => {
    setActiveId(id);
    trackSection(id);
    if (id === 'intro') {
      setShowCover(true);
      window.history.pushState(null, '', window.location.pathname);
    } else {
      setShowCover(false);
      window.history.pushState(null, '', `#${id}`);
    }
  };

  // Show cover page if enabled
  if (showCover) {
    return (
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={currentUrl} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
        </Helmet>
        <CoverPage
          meta={proposalMeta}
          destinations={destinationsWithApprovedVenues}
          heroImages={heroImagesForCover}
          onEnter={() => handleNavigate('overview')}
        />
      </ThemeContext.Provider>
    );
  }
  
  // Render content based on activeId
  const renderContent = () => {
    // The Comparison page is entirely budget figures, so it falls back to the overview when hidden.
    switch (activeId === 'compare' && !showBudget ? 'overview' : activeId) {
      case 'intro':
        return (
          <CoverPage
            meta={proposalMeta}
            destinations={destinationsWithApprovedVenues}
            heroImages={heroImagesForCover}
            onEnter={() => handleNavigate('overview')}
          />
        );
      case 'overview':
        // Map filtered destinations with their first images
        const destinationsWithImages = destinationsWithApprovedVenues.map((dest: { id: string; city: string; country: string; heroImage?: string; tagline?: string }) => ({
          ...dest,
          heroImage: getDestinationFirstImage(dest.id) || dest.heroImage,
        }));
        return (
          <Overview
            eventName={proposalMeta.event_name || eventName || 'Event Proposal'}
            clientName={proposalMeta.client_name || clientName || ''}
            destinations={destinationsWithImages}
            layoutType={overviewLayout || 'multi-column'}
            onNavigate={(destinationId) => handleNavigate(destinationId)}
          />
        );
      case 'agenda':
        return <Agenda agenda={proposalData?.agenda} />;
      case 'compare':
        const shortlistedHotelIds = new Set(clientShortlist.map(item => item.hotelId));
        return (
          <Comparison
            destinations={destinationsWithApprovedVenues}
            approvedVenuesByDestination={approvedVenuesByDestination}
            venueDataByDestination={venueDataByDestination}
            destinationContentByDestination={destinationContentByDestination}
            groupSize={eventData?.expected_attendees || 50}
            budgetMin={eventData?.budget_min ?? undefined}
            budgetMax={eventData?.budget_max ?? undefined}
            shortlistedHotelIds={shortlistedHotelIds}
            shareLinkToken={token}
            sessionId={session?.sessionId}
            eventId={eventData?.id}
            onAddToShortlist={async (hotel) => {
              try {
                // Get the hotel image from approvedVenuesByDestination
                const venues = approvedVenuesByDestination[hotel.destinationId] || [];
                const venueData = venues.find(v => v.id === hotel.id);
                const hotelImage = venueData?.image || hotel.destinationImage || hotel.image;
                
                await proposalService.addToClientShortlist(
                  token!,
                  hotel.id,
                  hotel.destinationId,
                  session?.sessionId
                );
                // Reload shortlist
                await loadClientShortlist(token!, session?.sessionId);
              } catch (err) {
                console.error('[ProposalView] Failed to add to shortlist:', err);
                // TODO: Show error notification
              }
            }}
            onNavigate={handleNavigate}
          />
        );
      case 'sign-off':
        return (
          <SignOff
            shortlistedHotels={clientShortlist}
            onRemoveFromShortlist={async (hotelId) => {
              try {
                await proposalService.removeFromClientShortlist(
                  token!,
                  hotelId,
                  session?.sessionId
                );
                // Reload shortlist
                await loadClientShortlist(token!, session?.sessionId);
              } catch (err) {
                console.error('[ProposalView] Failed to remove from shortlist:', err);
                // TODO: Show error notification
              }
            }}
            onSendFeedback={async (name, comments) => {
              await proposalService.sendFeedback(
                token!,
                name,
                comments,
                session?.sessionId
              );
            }}
            onBookReviewCall={() => {
              const link = calendlyLink || 'https://calendly.com/moniker-partners/review-call';
              window.open(link, '_blank');
            }}
            onNavigate={handleNavigate}
            showEstimates={showBudget}
          />
        );
      default:
        // Destination detail page - find destination by ID
        const selectedDestination = destinations.find((d: { id: string; city: string; country: string; heroImage?: string; tagline?: string }) => d.id === activeId) || displayDestinations.find((d: { id: string; city: string; country: string; heroImage?: string; tagline?: string }) => d.id === activeId);
        if (selectedDestination) {
          // Get approved venues for this destination
          const hotels = approvedVenuesByDestination[selectedDestination.id] || [];
          const venueData = venueDataByDestination[selectedDestination.id] || [];
          
          // Get destination content if available
          const content = destinationContentByDestination[selectedDestination.id];
          
          // Map images/video from destination content to media format
          let media: any = undefined;
          const mediaLayout = content?.media_layout;
          const hasVideo = !!content?.video;
          const hasImages = content?.images && content.images.length > 0;
          
          // Determine effective layout: if auto/default, prioritize video if it exists, otherwise gallery
          const effectiveLayout = mediaLayout || (hasVideo ? 'video' : hasImages ? 'gallery' : null);
          
          // If video exists and effective layout is 'video', use video hero with background mode (auto-play)
          if (hasVideo && effectiveLayout === 'video') {
            const videoItem = {
              type: 'video' as const,
              url: content.video.url,
              thumbnail: content.video.thumbnail || content.images?.[0]?.url,
            };
            media = {
              layout: 'video-hero',
              videoMode: 'background', // Auto-play in background, no overlay/play button
              items: [videoItem],
            };
          } else if (hasImages) {
            const imageItems = content.images.map((img: any) => ({
              type: 'image' as const,
              url: typeof img === 'string' ? img : img.url,
              caption: typeof img === 'string' ? undefined : img.caption,
            }));
            
            // Determine layout based on effective layout preference
            let layout: 'auto' | 'collage' | 'carousel' | 'video-hero' | 'hero' = 'auto';
            if (effectiveLayout === 'grid') {
              // Grid layout uses first 3 images in collage format
              layout = 'collage';
            } else {
              // Gallery or auto defaults to carousel for multiple images, hero for single
              layout = imageItems.length > 1 ? 'carousel' : 'hero';
            }
            
            media = {
              layout,
              items: effectiveLayout === 'grid' ? imageItems.slice(0, 3) : imageItems,
            };
          }
          
          // Get activities for this destination
          const activities = activitiesByDestination[selectedDestination.id] || [];
          
          // Get events for this destination
          const events = eventsByDestination[selectedDestination.id] || [];
          
          // Map destination data to Destination component format
          const destinationData = {
            id: selectedDestination.id,
            city: selectedDestination.city,
            country: selectedDestination.country,
            heroImage: content?.images?.[0]?.url || (typeof content?.images?.[0] === 'string' ? content.images[0] : undefined) || selectedDestination.heroImage,
            tagline: content?.short_description || selectedDestination.tagline,
            description: content?.short_description || undefined, // Use short_description as the main description
            pros: Array.isArray(content?.key_pros) ? content.key_pros : [],
            hotels: hotels.length > 0 ? hotels : undefined, // Use approved venues if available
            activities: activities.length > 0 ? activities : undefined, // Use fetched activities
            events: events.length > 0 ? events : undefined, // Use fetched events
            pricing: undefined, // Will use placeholder
            media: media,
          };
          
          const shortlistedHotelIds = new Set(clientShortlist.map(item => item.hotelId));
          
          return (
            <Destination
              destination={destinationData}
              groupSize={eventData?.expected_attendees || 50}
              venueData={venueData}
              destinationContent={content}
              shareLinkToken={token}
              sessionId={session?.sessionId}
              eventId={linkInfo?.event_id}
              showBudget={showBudget}
              onSelectPackage={(destId, hotelId) => {
                // TODO: Handle package selection
              }}
              onAddToFavorites={async (destinationId, hotelId) => {
                try {
                  await handleAddToClientShortlist(hotelId, destinationId);
                } catch (err) {
                  console.error('[ProposalView] Failed to add to favorites:', err);
                }
              }}
              shortlistedHotelIds={shortlistedHotelIds}
              onViewHotelDetails={trackHotelView}
              eventDateType={(eventData?.date_type as 'date_range' | 'month') || 'date_range'}
              eventMonth={eventData?.event_month || null}
              eventStartDate={eventData?.start_date || null}
              eventEndDate={eventData?.end_date || null}
              eventDateDisplayOverride={eventData?.date_display_override || null}
              temperatureUnit={eventData?.temperature_unit || 'celsius'}
              distanceUnit={eventData?.distance_unit || 'kilometers'}
              onReturnToProposal={() => handleNavigate('overview')}
            />
          );
        }
        return (
          <div className="h-full flex items-center justify-center">
            <p className="text-teal/60">Destination not found</p>
          </div>
        );
    }
  };
  
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <ProposalLayout
        activeId={activeId}
        onNavigate={handleNavigate}
        destinations={destinationsWithApprovedVenues}
        showAgenda={!!proposalData?.agenda}
        showComparison={showBudget}
        meta={{
          eventName: proposalMeta.event_name || eventName || 'Event Proposal',
          clientName: proposalMeta.client_name || clientName || '',
          dates: proposalMeta.dates,
        }}
      >
        {renderContent()}
      </ProposalLayout>
    </ThemeContext.Provider>
  );
}
